import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from '@tabler/icons-react';
import { Button } from './Button.js';
import { IconButton } from './IconButton.js';
import { cx } from '../utils/cx.js';
import { useOverlayFocusTrap } from '../utils/useOverlayFocusTrap.js';
import { isTopOverlay, popOverlay, pushOverlay } from '../utils/overlayEscStack.js';
import { usePresence } from '../utils/usePresence.js';
import { overlayPresenceProps } from '../hooks/mistRail.js';
import styles from './Modal.module.css';

/** Enter spring.slow (280ms); exit spring.slow-exit (180ms) — fallback ≥ enter. */
const MODAL_MS = 280;

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

export type ModalProps = {
  opened: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: ModalSize;
  /** Extra class on the modal panel. */
  className?: string;
  /** Extra class on the modal body. */
  bodyClassName?: string;
  /** Full-bleed strip under header row (e.g. wizard progress as bottom edge). */
  headerBottom?: ReactNode;
};

export function Modal({
  opened,
  onClose,
  title,
  children,
  footer,
  size = 'sm',
  className,
  bodyClassName,
  headerBottom,
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const { mounted, visible } = usePresence(opened, { ms: MODAL_MS, panelRef });
  useOverlayFocusTrap(mounted && visible, panelRef);

  useEffect(() => {
    if (!mounted || !visible) return;
    const stackId = pushOverlay();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Nested overlays (drawers/modals above): only the top-most closes.
      if (!isTopOverlay(stackId)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onCloseRef.current();
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      popOverlay(stackId);
    };
  }, [mounted, visible]);

  if (!mounted) return null;

  const node = (
    <div
      className={cx(styles.backdrop, visible && styles.backdropVisible)}
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        ref={panelRef}
        className={cx(styles.panel, styles[size], visible && styles.panelVisible, className)}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        {...overlayPresenceProps(visible)}
      >
        <div className={cx(styles.header, headerBottom != null && styles.headerWithBottom)}>
          <div className={styles.headerMain}>
            <span id={titleId} className={styles.title}>
              {title}
            </span>
            <IconButton
              type="button"
              variant="ghost"
              size="md"
              className={styles.close}
              aria-label="Fermer"
              onClick={onClose}
            >
              <IconX size={22} stroke={1.5} aria-hidden />
            </IconButton>
          </div>
          {headerBottom != null ? <div className={styles.headerBottom}>{headerBottom}</div> : null}
        </div>
        <div className={cx(styles.body, bodyClassName)} data-wp-mist-scroll="">{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

export type ConfirmModalFooterProps = {
  onCancel: () => void;
  onConfirm: () => void;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

export function ConfirmModalFooter({ onCancel, onConfirm, confirmLabel = 'Confirmer', cancelLabel = 'Annuler', danger }: ConfirmModalFooterProps) {
  return (
    <>
      <Button variant="ghost" onClick={onCancel}>
        {cancelLabel}
      </Button>
      <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm}>
        {confirmLabel}
      </Button>
    </>
  );
}
